import React, { useState, useEffect } from 'react';
import '../styles/AddLead.css';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  packLeadBookingFrequency,
  parseLeadBookingFrequency,
} from '../utils/leadSource';

const EXT_CONTENT_TYPES = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  doc: 'application/msword',
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  txt: 'text/plain',
};

const leadFileContentType = (file) => {
  const type = String(file?.type || '').trim();
  if (type) return type;
  const ext = String(file?.name || '')
    .split('.')
    .pop()
    .toLowerCase();
  return EXT_CONTENT_TYPES[ext] || 'application/octet-stream';
};

const fileDisplayName = (keyOrUrl) => {
  const raw = String(keyOrUrl || '').trim();
  if (!raw) return 'File';
  try {
    const path = raw.includes('://') ? new URL(raw).pathname : raw;
    const part = decodeURIComponent(path.split('/').pop() || raw);
    return part.replace(/^\d{10,}-[0-9a-f-]{8,}-/i, '') || part;
  } catch {
    return raw.split('/').pop() || 'File';
  }
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });

const AddLead = ({ leadData, onSave, onCancel }) => {
  const isEdit = Boolean(leadData?.id);
  const [formData, setFormData] = useState({
    clientName: '',
    address: '',
    postcode: '',
    primaryContact: '',
    dealSource: 'Website',
    contactNumber: '',
    contactEmail: '',
    label: 'Opportunity/Assessment',
    serviceType: '',
    anticipatedCloseDate: '',
    description: '',
    files: [], // multiple files
  });

  const [services, setServices] = useState([]);
  const [assignedBranch, setAssignedBranch] = useState(null);
  /** idle | loading | found | failed */
  const [branchLookup, setBranchLookup] = useState('idle');
  const [branchHint, setBranchHint] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingFiles, setExistingFiles] = useState([]);
  const [existingFilesLoading, setExistingFilesLoading] = useState(false);
  useEffect(() => {
    if (!leadData) return;
    const packed = parseLeadBookingFrequency(leadData.bookingFrequency);
    setFormData((prev) => ({
      ...prev,
      clientName: leadData.clientName || '',
      address: leadData.address || '',
      postcode: leadData.postcode || '',
      contactNumber: leadData.contactNumber || '',
      contactEmail: leadData.contactEmail || '',
      description: leadData.message || '',
      label:
        packed.label ||
        leadData.stage?.name ||
        prev.label,
      dealSource: leadData.source || packed.source || prev.dealSource,
      primaryContact: leadData.clientName || prev.primaryContact,
      serviceType: leadData.serviceType || '',
      anticipatedCloseDate: leadData.preferredDate
        ? String(leadData.preferredDate).slice(0, 10)
        : prev.anticipatedCloseDate,
    }));
    if (leadData.branchId) {
      setAssignedBranch({
        id: leadData.branchId,
        name: leadData.branchName || 'Assigned branch',
      });
      setBranchLookup(leadData.branchName ? 'found' : 'idle');
    }
  }, [leadData]);

  useEffect(() => {
    if (!leadData?.id) {
      setExistingFiles([]);
      setExistingFilesLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadExistingFiles = async () => {
      setExistingFilesLoading(true);
      try {
        const data = await graphqlRequest(
          `
            query FindOneLeadFiles($id: String!) {
              findOneBooking(id: $id, includeFileUrls: true) {
                id
                uploads
                signUrls
              }
            }
          `,
          { id: leadData.id },
        );
        if (cancelled) return;
        const booking = data?.findOneBooking || {};
        let keys = Array.isArray(booking.uploads) ? booking.uploads.filter(Boolean) : [];
        let urls = Array.isArray(booking.signUrls) ? booking.signUrls : [];
        if (keys.length > 0 && urls.filter(Boolean).length === 0) {
          const resolved = await graphqlRequest(
            `
              query ResolveLeadFileUrls($bookingId: String!, $keys: [String!]!) {
                resolveBookingFileUrls(bookingId: $bookingId, keys: $keys)
              }
            `,
            { bookingId: leadData.id, keys },
          );
          urls = Array.isArray(resolved?.resolveBookingFileUrls)
            ? resolved.resolveBookingFileUrls
            : [];
        }
        if (cancelled) return;
        setExistingFiles(
          keys.map((key, index) => ({
            key,
            url: urls[index] || '',
            name: fileDisplayName(key),
          })),
        );
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load lead files', err);
          setExistingFiles(
            (Array.isArray(leadData.uploads) ? leadData.uploads : []).map((key) => ({
              key,
              url: '',
              name: fileDisplayName(key),
            })),
          );
        }
      } finally {
        if (!cancelled) setExistingFilesLoading(false);
      }
    };

    loadExistingFiles();
    return () => {
      cancelled = true;
    };
  }, [leadData?.id]);

  useEffect(() => {
    fetchServices();
  }, []);

  // Resolve nearest branch when postcode is entered (AU 4-digit).
  useEffect(() => {
    const pc = String(formData.postcode || '').trim();
    if (!/^\d{4}$/.test(pc)) {
      if (!isEdit) {
        setAssignedBranch(null);
        setBranchHint('');
        setBranchLookup('idle');
      }
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setBranchHint('');
      setBranchLookup('loading');
      try {
        const query = `
          query NearestBranchForPostcode($postcode: String!, $limit: Int) {
            getNearestBranches(postcode: $postcode, limit: $limit) {
              distanceKm
              branch { id name postcode }
            }
          }
        `;
        const data = await graphqlRequest(query, {
          postcode: pc,
          limit: 1,
        });
        if (cancelled) return;

        const nearest = data?.getNearestBranches?.[0]?.branch || null;
        if (nearest?.id) {
          setAssignedBranch(nearest);
          setBranchLookup('found');
          setBranchHint(
            nearest.postcode && String(nearest.postcode) !== pc
              ? `Assigned to nearest branch (${nearest.name}).`
              : '',
          );
          return;
        }

        // Fallback: company branch list (also covers older backends).
        const companyData = await graphqlRequest(`
          query CompanyBranchesForLead {
            getMyCompanyBranches { id name postcode isActive }
          }
        `);
        if (cancelled) return;
        const rows = Array.isArray(companyData?.getMyCompanyBranches)
          ? companyData.getMyCompanyBranches
          : [];
        const list = rows.filter((b) => b?.isActive !== false);
        const pick =
          list.find((b) => String(b?.postcode || '').trim() === pc) ||
          list[0] ||
          rows[0] ||
          null;

        if (pick?.id) {
          setAssignedBranch(pick);
          setBranchLookup('found');
          setBranchHint('Using company default branch.');
          return;
        }

        setAssignedBranch(null);
        setBranchLookup('failed');
        setBranchHint(
          'No company branches found — create a branch in Branch Management.',
        );
      } catch (e) {
        if (cancelled) return;
        console.error('Branch assignment failed', e);
        setAssignedBranch(null);
        setBranchLookup('failed');
        setBranchHint(
          e?.message ||
            'Unable to find a branch — create one in Branch Management.',
        );
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.postcode, isEdit]);

  const fetchServices = async () => {
    try {
      const query = `
        query GetService {
          getService(includeStats: false) {
            id
            service
            description
          }
        }
      `;
      const data = await graphqlRequest(query);
      setServices(data?.getService || []);
    } catch (err) {
      console.error('Error fetching services:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'file' ? Array.from(files) : value,
    }));
  };

  const uploadLeadFile = async (file) => {
    const contentType = leadFileContentType(file);
    if (file.size > 15 * 1024 * 1024) {
      throw new Error(`${file.name} must be 15MB or smaller`);
    }
    const dataBase64 = await readFileAsBase64(file);
    const data = await graphqlRequest(
      `
        mutation UploadBookingFileContent(
          $fileName: String!
          $contentType: String!
          $dataBase64: String!
        ) {
          uploadBookingFileContent(
            fileName: $fileName
            contentType: $contentType
            dataBase64: $dataBase64
          ) {
            key
          }
        }
      `,
      {
        fileName: file.name,
        contentType,
        dataBase64,
      },
      { timeout: 120000 },
    );
    const key = data?.uploadBookingFileContent?.key;
    if (!key) {
      throw new Error(`Failed to upload ${file.name}`);
    }
    return key;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const bookingInput = {
        fullName: formData.clientName,
        email: formData.contactEmail,
        phone: formData.contactNumber,
        address: formData.address,
        postcode: formData.postcode,
        service: formData.serviceType,
        // Persist real source + pipeline label; list Source column reads the first part.
        bookingFrequency: packLeadBookingFrequency(
          formData.dealSource,
          formData.label,
        ),
        message: formData.description,
        preferredDate: formData.anticipatedCloseDate,
      };

      // Remove empty fields
      const cleanedInput = Object.fromEntries(
        Object.entries(bookingInput).filter(([_, v]) => v),
      );

      let fileKeys = [];
      if (formData.files.length > 0) {
        fileKeys = await Promise.all(formData.files.map((file) => uploadLeadFile(file)));
        if (fileKeys.some((key) => !key)) {
          throw new Error('Could not upload the selected file(s).');
        }
      }

      let savedBooking;

      if (isEdit) {
        // UPDATE existing lead
        const mutation = `
          mutation UpdateBooking($updateBookingInput: UpdateBookingInput!, $fileKeys: [String!]) {
            updateBooking(updateBookingInput: $updateBookingInput, fileKeys: $fileKeys) {
              id
              fullName
              phone
              email
              address
              postcode
              status
              bookingFrequency
              message
              preferredDate
              branchId
              uploads
              stage {
                id
                name
              }
              bookingServices {
                id
                service {
                  id
                  service
                }
              }
            }
          }
        `;
      
        const data = await graphqlRequest(mutation, {
          updateBookingInput: {
            id: leadData.id,
            ...cleanedInput,
            ...(assignedBranch?.id ? { branchId: assignedBranch.id } : {}),
            ...(fileKeys.length > 0 ? { uploads: fileKeys } : {}),
          },
          fileKeys: fileKeys.length > 0 ? fileKeys : undefined,
        });
        savedBooking = data?.updateBooking;
      } else {
        // CREATE new lead
        const mutation = `
          mutation CreateLeadAsAdmin($createBookingInput: CreateBookingInput!, $fileKeys: [String!], $branchId: String) {
            createLeadAsAdmin(createBookingInput: $createBookingInput, fileKeys: $fileKeys, branchId: $branchId) {
              id
              fullName
              phone
              email
              address
              postcode
              status
              bookingFrequency
              message
              preferredDate
              branchId
              uploads
              stage {
                id
                name
              }
              bookingServices {
                id
                service {
                  id
                  service
                }
              }
            }
          }
        `;
      
        const data = await graphqlRequest(mutation, {
          createBookingInput: cleanedInput,
          fileKeys: fileKeys.length > 0 ? fileKeys : undefined,
          branchId: assignedBranch?.id ?? null,
        });
        savedBooking = data?.createLeadAsAdmin;
      }
      
      if (savedBooking) {
        if (fileKeys.length > 0) {
          const savedKeys = Array.isArray(savedBooking.uploads)
            ? savedBooking.uploads
            : [];
          const missing = fileKeys.filter((key) => !savedKeys.includes(key));
          if (missing.length) {
            throw new Error(
              'The document was uploaded but not saved on this lead. Please try again.',
            );
          }
        }
        onSave({
          ...savedBooking,
          branchName: assignedBranch?.name || '',
        });
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.message || (isEdit ? 'Failed to update lead. Please try again.' : 'Failed to create lead. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="addlead-container">
      <div className="addlead-box">
        <div className="addlead-header">
          <h2>{isEdit ? 'Edit Lead' : 'Add Lead'}</h2>
        </div>
        {error && (
          <div
            className="error-message"
            style={{ color: 'red', padding: '10px', marginBottom: '10px' }}
          >
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="addlead-form">
          {/* LEFT */}
          <div className="form-left">
            <label>
              Client Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              name="clientName"
              value={formData.clientName}
              onChange={handleChange}
              required
            />

            <label>
              Postcode <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              name="postcode"
              inputMode="numeric"
              pattern="[0-9]{4}"
              maxLength={4}
              value={formData.postcode}
              onChange={(e) => {
                const v = String(e.target.value || '').replace(/\D+/g, '');
                setFormData((prev) => ({ ...prev, postcode: v.slice(0, 4) }));
              }}
              required
            />

            <label>Assigned Branch (auto)</label>
            <input
              type="text"
              value={
                branchLookup === 'loading'
                  ? 'Finding nearest branch…'
                  : assignedBranch?.name ||
                    (assignedBranch?.id ? 'Branch assigned' : null) ||
                    (branchLookup === 'failed'
                      ? 'No branch matched'
                      : 'Enter postcode to assign')
              }
              disabled
            />
            {branchHint ? (
              <div
                style={{ fontSize: '12px', color: '#64748b', marginTop: '-6px' }}
              >
                {branchHint}
              </div>
            ) : null}

            <label>
              Deal Source <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              name="dealSource"
              value={formData.dealSource}
              onChange={handleChange}
              required
            >
              <option>Website</option>
              <option>Facebook</option>
              <option>Google</option>
              <option>Tiktok</option>
              <option>Instagram</option>
              <option>X</option>
              <option>LinkedIn</option>
              <option>Others</option>
            </select>

            <label>
              Contact Number <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleChange}
              required
            />

            <label>
              Label <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              name="label"
              value={formData.label}
              onChange={handleChange}
              required
            >
              <option>Opportunity/Assessment</option>
              <option>Need Analysis</option>
              <option>Proposal/Price quotation</option>
              <option>Negotiation/Review</option>
              <option>Closed won</option>
              <option>Closed Lost</option>
            </select>

            <label>
              Description <span style={{ color: 'red' }}>*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
            />
          </div>

          {/* RIGHT */}
          <div className="form-right">
            <label>
              Address <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
            />

            <label>
              Primary Contact <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              name="primaryContact"
              value={formData.primaryContact}
              onChange={handleChange}
              required
            />

            <label>
              Service Type <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              name="serviceType"
              value={formData.serviceType}
              onChange={handleChange}
              required
            >
              <option value="">Select Service</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.service}
                </option>
              ))}
            </select>

            <label>
              Contact Email <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="email"
              name="contactEmail"
              value={formData.contactEmail}
              onChange={handleChange}
              required
            />

            <label>
              Anticipated Close Date <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="date"
              name="anticipatedCloseDate"
              value={formData.anticipatedCloseDate}
              onChange={handleChange}
              required
            />

            <label htmlFor="lead-edit-files">Files (Optional)</label>
            {isEdit ? (
              <div className="lead-existing-files">
                {existingFilesLoading ? (
                  <div className="lead-existing-files__empty">Loading documents…</div>
                ) : existingFiles.length > 0 ? (
                  <ul className="lead-existing-files__list">
                    {existingFiles.map((file) => (
                      <li key={file.key}>
                        {file.url ? (
                          <a href={file.url} target="_blank" rel="noreferrer">
                            {file.name}
                          </a>
                        ) : (
                          <span>{file.name}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="lead-existing-files__empty">
                    No documents saved on this lead yet.
                  </div>
                )}
              </div>
            ) : null}
            <input
              id="lead-edit-files"
              type="file"
              name="files"
              multiple
              onChange={handleChange}
            />
            {formData.files.length > 0 && (
              <div
                style={{ fontSize: '12px', marginTop: '5px', color: '#666' }}
              >
                New: {formData.files.map((file) => file.name).join(', ')}
              </div>
            )}

            <div className="form-footer">
              <button type="button" className="cancel-btn" onClick={onCancel}>
                Cancel
              </button>
              <button type="submit" className="save-btn" disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddLead;
